import Uppy from '@uppy/core'
import Dashboard from '@uppy/dashboard'
import AwsS3Multipart from '@uppy/aws-s3-multipart'
import Polish from '@uppy/locales/lib/pl_PL'

import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'

// Most views load this drag-drop area via a full page/Turbolinks navigation
// (documents/new.html.erb, tutorial_documents/edit_file.html.erb, ...), but
// tutorial_documents/_edit_file_for_many.html.erb injects it into a Bootstrap
// modal via AJAX instead - turbolinks:load never fires for that, so relying
// on it alone silently leaves the widget uninitialized (an empty div, no
// visible file input) whenever it's opened from a modal. Mirrors
// uppy_comment_file_upload.js's MutationObserver-based init, which picks up
// the drag-drop area whenever it appears in the DOM regardless of how it
// got there, and tears the instance down again if its container is removed
// (e.g. the modal is closed without saving).
let uppyInstance;
let trackedDragDropArea;

function destroyUppyInstance() {
    if (uppyInstance) {
        uppyInstance.destroy();
        uppyInstance = null;
    }
    trackedDragDropArea = null;
}

function initDocumentUppyIfPresent() {
    const dragDropArea = document.querySelector('#uppy-drag-drop-area')

    if (!dragDropArea || dragDropArea.dataset.uppyInitialized === 'true') return;
    dragDropArea.dataset.uppyInitialized = 'true';
    trackedDragDropArea = dragDropArea;

    uppyInstance = new Uppy({
        autoProceed: true,
        restrictions: {maxNumberOfFiles: parseInt(dragDropArea.dataset.maxNumberOfFiles)},
        locale: Polish
    })

    uppyInstance.use(Dashboard, {
        inline: true,
        target: '#uppy-drag-drop-area',
        showProgressDetails: true,
        proudlyDisplayPoweredByUppy: false,
        height: 300,
        width: '100%',
        maxFileSize: dragDropArea.dataset.maxFileSize,
        maxNumberOfFiles: parseInt(dragDropArea.dataset.maxNumberOfFiles),
        showRemoveButtonAfterComplete: true
    })

    uppyInstance.use(AwsS3Multipart, {
        id: 'AwsS3Multipart',

        mode: 'custom',

        shouldUseMultipart: (_file) => true,

        createMultipartUpload: async (file) => {
            const response = await fetch('/s3/multipart', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                },
                body: JSON.stringify({
                    filename: file.name,
                    type: file.type
                })
            })
            const result = await response.json()
            return {
                uploadId: result.uploadId,
                key: result.key
            }
        },

        signPart: async (file, {uploadId, key, partNumber}) => {
            const res = await fetch(`/s3/multipart/${uploadId}/${partNumber}?key=${encodeURIComponent(key)}`, {
                method: 'GET'
            })

            const {url} = await res.json()
            return {
                url,
                headers: {'Content-Type': 'application/octet-stream'}
            }
        },

        completeMultipartUpload: async (file, {key, uploadId, parts}) => {
            const response = await fetch(`/s3/multipart/${uploadId}/complete?key=${encodeURIComponent(key)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                },
                body: JSON.stringify({
                    parts,
                    model: dragDropArea.dataset.model,
                    tagId: dragDropArea.dataset.tagId,
                    token: dragDropArea.dataset.token
                })
            })
            const result = await response.json()
            uppyInstance.setFileState(file.id, {modelId: result.document_file_id})

            return {location: result.location}
        },

        abortMultipartUpload: async (file, {key, uploadId}) => {
            await fetch(`/s3/multipart/${uploadId}?key=${encodeURIComponent(key)}`, {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                }
            })
        }
    })

    uppyInstance.on('file-removed', (file) => {
        if (file.modelId) {
            fetch('/' + dragDropArea.dataset.modelInPaths + '/' + file.modelId, {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                }
            })
        }
    })
}

function handleDomMutation() {
    if (trackedDragDropArea && !document.body.contains(trackedDragDropArea)) {
        destroyUppyInstance();
    }
    initDocumentUppyIfPresent();
}

document.addEventListener('turbolinks:before-render', destroyUppyInstance);
document.addEventListener('DOMContentLoaded', initDocumentUppyIfPresent);
document.addEventListener('turbolinks:load', initDocumentUppyIfPresent);

// The pack tag executes before <body> exists (no defer), so document.body
// isn't available yet at top level - start observing once it is.
function startObservingBody() {
    if (!document.body) {
        setTimeout(startObservingBody, 0);
        return;
    }
    new MutationObserver(handleDomMutation).observe(document.body, {childList: true, subtree: true});
    initDocumentUppyIfPresent();
}

startObservingBody();
